import type {
	DefaultFeatureFlagValue,
	FeatureFlagValue,
} from '@atlassian/jira-feature-flagging-using-meta';
import { getMockedFeatureFlags } from '../mocks';
import {
	registerUnmockedFeatureAccess,
	registerMockedFeatureAccess,
} from '../usage-tracker/feature-flag-usage-tracker';

let testDefaultValue: boolean | undefined;

/**
 * for internal usage only
 */
export const setFeatureFlagDefaultsInTests = (newDefault: boolean | undefined) => {
	testDefaultValue = newDefault;
};

let defaultValues: Record<string, boolean | string> | undefined;
let defaultValuesLocation: Error | undefined;
let defaultValuesLocationReporter: ((unusedFlags: string) => Error) | undefined;
/**
 * configures "test defaults". Not to be used for any other purpose
 * @deprecated reserved to "support" migration to double feature flag testing.
 */
export const setFeatureFlagsDefaultValuesInTests = (values: Record<string, boolean | string>) => {
	defaultValues = values;

	const location = new Error('');
	defaultValuesLocation = location;
	Error.captureStackTrace(location, setFeatureFlagsDefaultValuesInTests);

	defaultValuesLocationReporter = (unusedFlags: string) => {
		// override error message to provide valuable information
		location.message = `🧪 this test no longer uses a few flags, please remove them from setFeatureFlagsDefaultValuesInTests:
Please remove 🔥: ${unusedFlags}

Details: https://go/jfe-docs/feature-flags#shadow-migration
Provided flags:${Object.keys(values).join(', ')}`;

		throw location;
	};
};

export const getFeatureFlagDefaultValuesMeta = () => ({
	values: defaultValues,
	location: defaultValuesLocation,
	reportLocation: defaultValuesLocationReporter,
});

// @ts-expect-error - TS2383 - Overload signatures must all be exported or non-exported.
function getFeatureFlagValue<T>(arg1: string, arg2: T): T;

/**
 * @deprecated Use `expVal` or `fg` instead - see jira/docs/playbook/statsig.md for more details.
 *
 * @param defaultValue boolean 'true' value is not allowed go/ff-default
 */
export function getFeatureFlagValue<T extends FeatureFlagValue>(
	featureFlagName: string,
	defaultValue: T extends DefaultFeatureFlagValue ? T : never,
): T {
	const featureFlags = getMockedFeatureFlags();
	const flagValue = featureFlags[featureFlagName] ?? defaultValues?.[featureFlagName];

	if (flagValue === undefined) {
		registerUnmockedFeatureAccess(featureFlagName, defaultValue);
		if (defaultValue === false) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return (testDefaultValue ?? defaultValue) as T;
		}
		return defaultValue;
	}
	registerMockedFeatureAccess(featureFlagName);

	if (typeof flagValue === 'object' && 'value' in flagValue) {
		return flagValue.value;
	}
	return flagValue;
}

/**
 * @param defaultValue No other values allowed other than false go/ff-default
 */
export function ff(featureFlagName: string, defaultValue: false = false): boolean {
	const flagValue = getFeatureFlagValue(featureFlagName, defaultValue);

	const isValid = typeof flagValue === 'boolean';

	return isValid ? flagValue : defaultValue;
}

/**
 * @deprecated Use `expVal` instead - see jira/docs/playbook/statsig.md for more details.
 */
export const getMultivariateFeatureFlag = <T,>(
	featureFlagName: string,
	defaultValue: T,
	possibleValues: T[] = [],
): T => {
	// @ts-expect-error - TS2345 - Argument of type 'T' is not assignable to parameter of type 'FeatureFlagValue'.
	const flagValue = getFeatureFlagValue(featureFlagName, defaultValue);

	// @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'T'.
	const isValid = typeof flagValue === 'string' && possibleValues.includes(flagValue);

	// @ts-expect-error - TS2322 - Type 'string | T' is not assignable to type 'T'.
	return isValid ? flagValue : defaultValue;
};

export const getAllFeatureFlags = () => getMockedFeatureFlags();
